<template>
  <div>
    <!-- Display Image & Price -->
    <item-info-container
      :imageURL="firstBookingDetailItem?.product?.image"
      :salePrice="getFirstBookingDetailItemTotal(firstBookingDetailItem)"
      :salePriceVisibility="pageAmountsvisibility"
      :hidePrice="hidePriceConditional"
      :name="firstBookingDetailItem?.product?.name"
    />

    <div class="p-4 text-start">
      <div v-if="firstBookingDetailItem?.product?.description" class="mb-4">
        {{ firstBookingDetailItem?.product?.description }}
      </div>

      <div class="d-flex flex-row">
        <div class="d-flex flex-column scss-bold w-25">
          <span>Date:</span>
          <span>Time:</span>
          <span>Duration:</span>
          <span>Outlet:</span>
          <span v-if="bookingDetails.items.length !== 0">Booked:</span>
          <!-- <span>Branch:</span> -->
          <span v-if="!hidePriceConditional">Status:</span>
        </div>

        <div class="d-flex flex-column flex-grow-1 text-end text-truncate w-75">
          <span class="text-truncate"
            >&nbsp;{{ convertDateFormat(dateTimeInTimeZone.date) }}</span
          >
          <span class="text-truncate">&nbsp;{{ durationOfService }}</span>
          <span class="text-truncate"
            >&nbsp;{{
              formatMinutesUpperCase(bookingDetails.processingTime)
            }}</span
          >
          <span>{{ getBranchNameById(bookingDetails.branch) }}</span>
          <span class="text-truncate" v-if="bookingDetails.items.length !== 0"
            >&nbsp;{{
              firstBookingDetailItem?.serviceEmployees[0]?.employee?.firstName
            }}</span
          >
          <!-- <span class="text-truncate"> &nbsp;{{ branchName }} </span> -->
          <span
            class="text-truncate"
            v-if="!hidePriceConditional"
          >
            &nbsp;{{ formatTicketStatus(bookingDetails.status) }}</span
          >
        </div>
      </div>
    </div>

    <!-- Enter Remarks -->
    <list-header title="Remarks" />

    <div class="p-3">
      <textarea
        class="form-control style-textarea"
        rows="5"
        :value="bookingDetails.remark"
        disabled
      ></textarea>
    </div>

    <!-- Term & Conditions -->
    <terms-and-conditions />

    <!-- Payment Details -->
    <list-header
      v-if="pageAmountsvisibility"
      title="Payment" />

    <div
      v-if="pageAmountsvisibility"
      class="p-4 style-payment-summary d-flex flex-row"
      style="line-height: 30px"
    >
      <div class="d-flex flex-column scss-bold text-start pe-3">
        <span>Payment Method:</span>
      </div>
      <div class="d-flex flex-column text-end flex-grow-1">
        <span>{{ paymentMethod }}</span>
      </div>
    </div>

    <!-- Error message -->
    <div class="style-error-message px-3">
      <!--
      Message to display if booking is not book via CWA(doesn't matter what payment method).
      `isOnlineBooking` value is used to check whether it's book via CWA
      -->
      <p v-if="bookingDetails.isOnlineBooking === false">
        This appointment is booked in store, kindly contact the person in charge
        for any changes.
      </p>
      <!-- Message to display if booking is book via CWA and involve payment gateway -->
      <p v-else-if="bookingDetails.onlineBookingPayment?.ticketId > 0">
        Your appointment cannot be canceled, please contact the merchant to
        cancel and refund. Thank you
      </p>
      <!--
        Message to display if is book via CWA and it's book with prepaid service.
        Note:
        1. Currently CWA don't support purchase/book multiple services, so prepaid service is purchase via wess-legacy
        2. If it's not CWA booking, it's should display if-else first condition msg, below scenario for now will only happened for book via CWA case.
      -->
      <p v-else-if="firstBookingDetailItem?.prepaiditemId > 0">
        This appointment is booked via My Package, kindly contact the person in charge
        for any changes.
      </p>
    </div>

    <!-- Cancel Booking Button -->
    <div class="px-2" v-if="cancelButtonRedering(bookingDetails)">
      <default-button
        title="Cancel Booking"
        cssClass="scss-button-red"
        @click="handleCancelModal"
      />
    </div>
  </div>
</template>

<script>
import {
  defineAsyncComponent,
  markRaw
} from "vue";
import ListHeader from "@/components/ListHeader";
import DefaultButton from "@/components/button/DefaultButton";
import ItemInfoContainer from "@/components/ItemInfoContainer";
import TermsAndConditions from "@/components/TermsAndConditions";
import { mapGetters, mapMutations } from "vuex";
import { cancelBooking } from "@/services/booking";
import { getBranchNameById } from "@/common/helpers/BaseHelper";
import { useToast } from "vue-toastification";
import { openModal, closeModal } from "@/store/modal";

import { ticketStatus } from "@/common/constant/TicketStatus";
import {
  addMinutesToTime,
  formatMinutesUpperCase,
  convertDateTimeToTimezone,
  convertDateFormat,
} from "@/common/helpers/DateTimeHelper.js";

export default {
  name: "ReviewMyBooking",
  components: {
    ListHeader,
    DefaultButton,
    ItemInfoContainer,
    TermsAndConditions,
  },
  data() {
    return {
      bookingDetails: null,
    };
  },
  computed: {
    dateTimeInTimeZone() {
      const formattedDateTime = convertDateTimeToTimezone({
        dateTime: this.bookingDetails.date,
      });

      return {
        date: formattedDateTime.substring(0, formattedDateTime.indexOf(" ")),
        time: formattedDateTime.substring(formattedDateTime.indexOf(" ") + 1),
      };
    },
    durationOfService() {
      return `${this.dateTimeInTimeZone.time} - ${addMinutesToTime(
        this.dateTimeInTimeZone.time,
        this.bookingDetails.processingTime
      )}`;
    },
    paymentMethod() {
      return this.bookingDetails.onlineBookingPayment?.method
        ? "Credit/Debit Card"
        : "Pay at store";
    },
    branchName() {
      return this.branches.find(
        (branch) => branch.id === this.bookingDetails.branch
      ).name;
    },
    firstBookingDetailItem() {
      return this.bookingDetails.items[0] ?
        this.bookingDetails.items[0] : null;
    },
    pageAmountsvisibility() {
      // determine whether to hide any amount figure - payment detail, service value, service price and etc
      // out of 4 case, only 3. My account > My package > book with prepaid service need to hide
      if (
        this.bookingDetails.isOnlineBooking === true &&
        this.bookingDetails.onlineBookingPayment?.ticketId === undefined &&
        this.bookingDetails.items[0]?.prepaiditemId > 0
      ) {
        return false;
      }

      return true;
    },
    hidePriceConditional() {
      // only hide price if all of the following condition met
      //  1. if wess-legacy > consumer web app > hide service price is Yes
      //  2. booking is book via CWA and not involve payment gateway
      return this.init.hideServicePrice > 0 &&
        (
          this.bookingDetails.onlineBookingPayment?.ticketId === undefined ||
          this.bookingDetails.onlineBookingPayment?.ticketId === null
        );
    },
    ...mapGetters(
      [
        "init",
        "branches",
        "selectedBranch",
      ]
    ),
  },
  methods: {
    handleCancelModal() {
      openModal(
        {
          id: "cancelModal",
          title: "Cancel Booking",
        },
        markRaw(defineAsyncComponent(() => import("@/components/ModalContentComponent.vue"))),
        {
          content: "Are you sure to cancel this booking?",
        },
        {},
        [
          {
            text: 'No',
            clickFn: () => {
              closeModal();
            },
          },
          {
            text: 'Yes',
            class: 'scss-button-red',
            clickFn: this.cancelBooking,
          }
        ]
      );
    },
    async cancelBooking() {
      this.showLoading(true);

      // Form data
      const payload = {
        ticketIds: [this.bookingDetails.id],
        branchId: this.selectedBranch.id,
      };
      try {
        await cancelBooking(payload);
        this.showLoading(false);
        closeModal();
        this.goToBookingCanceled();
      } catch (error) {
        useToast().error("Issue occurred, failed to canceled booking.");
      }
      this.showLoading(false);
    },
    goToBookingCanceled() {
      this.$router.replace({ path: "/booking/cancel" });
    },
    formatTicketStatus(status) {
      return `${ticketStatus[status]}${
        parseInt(this.bookingDetails?.onlineBookingPayment?.amount) > 0
          ? "(Paid)"
          : ""
      }`;
    },
    cancelButtonRedering(bookingDetails) {
      // API URL: url/sale/saleTicket?limit=10&sort=date&sortBy=DESC&showDetail=1&statuses=RQ,OP,CF,CI,NS,BC&branch=x
      // Total 4 case
      // 1. Buy a service > pay at store
      //    - isOnlineBooking: true
      //    - isOnlinePayment: false,
      //    - onlineBookingPayment
      //      > paymentProvider: ""
      //      > paymentResponse: ""
      //    - items
      //      > prepaiditemId: 0
      // 2. Buy a service > payment gateway
      //    - isOnlineBooking: true
      //    - isOnlinePayment: false,
      //    - onlineBookingPayment
      //      > paymentProvider: ""
      //      > paymentResponse: ""
      //      > ticketId: xxxx
      //      > paymentId: 0
      //      > method: x
      //      > amount: xx.0000
      //      > tax: xx.0000
      //      > balance: xx.0000
      //    - items
      //      > prepaiditemId: xxxx
      // 3. My account > My package > book with prepaid service
      //    - isOnlineBooking: true
      //    - isOnlinePayment: false,
      //    - onlineBookingPayment
      //      > paymentProvider: ""
      //      > paymentResponse: ""
      //    - items
      //      > prepaiditemId: xxxx
      // 4. wess-legacy > apppointment 2.0 > create a booking
      //    - isOnlineBooking: false
      //    - isOnlinePayment: false,
      //    - onlineBookingPayment: KEY NOT EXIST
      //    - items
      //      > prepaiditemId: DEPEND ON HOW USER MANUALLY ADD WHAT TYPE OF SERVICE
      if (bookingDetails.isOnlineBooking === true) {
        if (
          bookingDetails.onlineBookingPayment?.ticketId > 0
        ) {
          return false;
        }

        return true;
      }

      return false;
    },
    getFirstBookingDetailItemTotal(item) {
      // scenario
      // 1. Buy a service > payment gateway
      if (this.bookingDetails.onlineBookingPayment?.ticketId > 0) {
        if (this.bookingDetails.onlineBookingPayment.saleTicket.items.length > 0) {
          return this.bookingDetails.onlineBookingPayment.saleTicket.items[0].total;
        }
      }

      // scenario
      // 1. Buy a service > pay at store
      // 2. My account > My package > book with prepaid service
      // 3. wess-legacy > apppointment 2.0 > create a booking
      return parseFloat(item.commissionValue) * parseFloat(item.unit);
    },
    getBranchNameById,
    formatMinutesUpperCase,
    convertDateFormat,
    ...mapMutations(["showLoading"]),
  },
  created() {
    this.bookingDetails = JSON.parse(sessionStorage.getItem("bookingDetails"));
  },
};
</script>

<style lang="scss" scoped>
.style-textarea {
  resize: none;
  -webkit-border-radius: 10px;
  -moz-border-radius: 10px;
  border-radius: 10px;
}

.style-payment-summary {
  font-size: 16px;
}

.style-error-message {
  color: $error-color;
  font-size: 14px;
}
</style>
